import { ArrowRightLeftIcon, CheckIcon, ChevronsUpDownIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { cn } from '../../../shared/lib/cn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../../../shared/ui/dropdown-menu';
import { toast } from '../../../shared/ui/sonner';

type OrgRef = { id: string; slug: string; name: string };

const initialOf = (name: string) => name.trim().charAt(0).toUpperCase();
const rowClass = 'h-9 gap-3 rounded-sm px-2 text-sm';

/**
 * The organization switcher at the foot of the sidebar. The active org lives in the URL, so switching is
 * navigating to `/o/<slug>`. Works at 240px and on the 64px rail (initial only, same accessible name). The
 * menu opens beside the sidebar; in the mobile drawer, where there is no room beside it, above the trigger.
 */
export function OrgSwitcherMenu({
  organizations,
  current,
  workspaceCount,
  collapsed,
  inDrawer,
  onCreate,
  onNavigate,
}: {
  organizations: readonly OrgRef[];
  current: OrgRef | null;
  /** Active workspaces of the current org the person can see. */
  workspaceCount: number;
  collapsed: boolean;
  inDrawer: boolean;
  onCreate: () => void;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation(['org', 'shell']);
  const navigate = useNavigate();
  const name = current?.name ?? t('switcher.none');
  const others = organizations.length > 1;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={current ? t('switcher.current', { name: current.name }) : t('switcher.none')}
        className={cn(
          'flex cursor-pointer items-center gap-3 rounded-sm text-left text-sidebar-foreground transition-colors duration-150 ease-standard hover:bg-sidebar-item-hover data-[state=open]:bg-sidebar-item-active',
          collapsed ? 'mx-auto justify-center p-0' : 'w-full min-w-0 py-2 pr-3 pl-2',
        )}
      >
        <span
          aria-hidden={collapsed ? undefined : 'true'}
          className="flex size-9 shrink-0 items-center justify-center rounded-sm bg-sidebar-accent text-sm font-bold text-sidebar-accent-foreground"
        >
          {initialOf(name)}
        </span>
        {collapsed ? null : (
          <>
            <span className="grid min-w-0 flex-1">
              <span className="truncate text-sm font-bold">{name}</span>
              <span className="truncate text-xs text-sidebar-muted-foreground">
                {t('shell:organization')}
              </span>
            </span>
            <ChevronsUpDownIcon
              aria-hidden="true"
              className="size-4 shrink-0 text-sidebar-muted-foreground"
            />
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={inDrawer ? 'top' : 'right'}
        align="end"
        // beside the sidebar: 20px clear of its edge (the trigger sits 12px inside it)
        sideOffset={inDrawer ? 8 : collapsed ? 20 : 32}
        className="w-75 max-w-[calc(100vw-2rem)] rounded-lg p-2"
      >
        {current ? (
          <>
            <div className="flex items-center gap-3 p-2 pb-3">
              <span
                aria-hidden="true"
                className="flex size-10 shrink-0 items-center justify-center rounded-sm bg-primary font-bold text-primary-foreground"
              >
                {initialOf(current.name)}
              </span>
              <span className="grid min-w-0">
                <span className="truncate font-bold text-heading">{current.name}</span>
                <span className="text-sm text-muted-foreground">
                  {t('switcher.workspaceCount', { count: workspaceCount })}
                </span>
              </span>
            </div>
            <DropdownMenuSeparator />
          </>
        ) : null}
        {others ? (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className={rowClass}>
              <ArrowRightLeftIcon aria-hidden="true" className="size-4 text-muted-foreground" />
              {t('shell:switchOrganization')}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent
              aria-label={t('shell:switchOrganization')}
              sideOffset={8}
              className="w-60 rounded-lg p-2"
            >
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {t('switcher.yours')}
              </DropdownMenuLabel>
              {organizations.map((org) => {
                const isCurrent = org.id === current?.id;
                return (
                  <DropdownMenuItem
                    key={org.id}
                    className={rowClass}
                    {...(isCurrent ? { 'aria-current': 'true' as const } : {})}
                    onSelect={() => {
                      if (isCurrent) return;
                      onNavigate?.();
                      navigate(`/o/${org.slug}`);
                      toast.success(t('switcher.switched', { name: org.name }));
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className="flex size-6 shrink-0 items-center justify-center rounded-xs bg-secondary text-xs font-bold text-secondary-foreground"
                    >
                      {initialOf(org.name)}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{org.name}</span>
                    {isCurrent ? <CheckIcon aria-hidden="true" className="size-4" /> : null}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ) : null}
        <DropdownMenuItem className={rowClass} onSelect={onCreate}>
          <PlusIcon aria-hidden="true" className="size-4 text-muted-foreground" />
          {t('switcher.create')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
